import pako from 'pako';

const normalizeUrl = (url) => (url?.endsWith('/') ? url.slice(0, -1) : url);

// Función para validar si una URL de WebSocket es válida
const validateWebSocketUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'ws:' || urlObj.protocol === 'wss:';
  } catch {
    return false;
  }
};

export class livenessWebSocketClient {
  constructor(processId, baseUrl, options = {}) {
    this.processId = processId;
    this.baseUrl = normalizeUrl(baseUrl);
    this.ws = null;
    this.isConnected = false;
    this.videoChunks = [];
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.isRecordingParallel = false;
    this.parallelVideoChunks = [];
    this.pendingVideoBlob = null;
    this.recordingStartTime = null; // Tiempo de inicio de grabación

    // Configuración de reintentos
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 2000; // 2 segundos
    this.currentRetry = 0;

    this.onConnected = options.onConnected || null;
    this.onBundleValidated = options.onBundleValidated || null;
    this.onVideoUploaded = options.onVideoUploaded || null;
    this.onComplete = options.onComplete || null;
    this.onError = options.onError || null;
    this.onVideoProgress = options.onVideoProgress || null;
    this.onClose = options.onClose || null;
  }

  isSocketConnected() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  connect() {
    return new Promise((resolve, reject) => {
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.log('[WS] Ya conectado');
          resolve();
          return;
        }

        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.log('[WS] Conexión en curso, esperando...');
          const timeout = setTimeout(() => {
            console.error('[WS] Timeout esperando conexión');
            reject(new Error('WebSocket connection timeout'));
          }, 10000); // Aumentado a 10s

          const checkConnection = setInterval(() => {
            if (this.ws.readyState === WebSocket.OPEN) {
              clearInterval(checkConnection);
              clearTimeout(timeout);
              console.log('[WS] ✅ Conectado (esperando)');
              resolve();
            } else if (this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
              clearInterval(checkConnection);
              clearTimeout(timeout);
              console.error('[WS] ❌ Conexión cerrada durante espera');
              reject(new Error('WebSocket closed during connection attempt'));
            }
          }, 100);
          return;
        }

        if (this.ws) {
          console.log('[WS] Cerrando conexión anterior');
          try { this.ws.close(); } catch (e) { console.warn('[WS] Error cerrando WS anterior:', e); }
          this.ws = null;
        }

        const wsProtocol = this.baseUrl.startsWith('https') ? 'wss' : 'ws';
        const wsUrl = this.baseUrl.replace(/^https?/, wsProtocol);
        const fullUrl = `${wsUrl}/ws/liveness/${this.processId}`;

        console.log('[WS] 🔌 Conectando a:', fullUrl);
        this.ws = new WebSocket(fullUrl);

        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            console.error('[WS] ⏱️ Timeout de conexión (10s)');
            this.ws.close();
            reject(new Error('WebSocket connection timeout after 10s'));
          }
        }, 10000);

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          this.isConnected = true;
          console.log('[WS] ✅ Conectado exitosamente');
          resolve();
        };

        this.ws.onmessage = (event) => this._handleMessage(event);

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          this.isConnected = false;
          console.error('[WS] ❌ Error de conexión:', {
            url: fullUrl,
            readyState: this.ws?.readyState,
            error: error
          });

          if (this.onError) {
            this.onError({
              type: 'CONNECTION_ERROR',
              message: 'No se pudo conectar al servidor WebSocket. Verifica tu conexión y la URL del servicio.',
              error,
              url: fullUrl
            });
          }

          // NO rechazar aquí si ya se conectó
          if (this.ws.readyState === WebSocket.CONNECTING) {
            reject(new Error('WebSocket connection failed'));
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          this.isConnected = false;
          console.log('[WS] 🔌 Desconectado:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });

          if (this.onClose) this.onClose(event);
        };
      } catch (error) {
        console.error('[WS] ❌ Error inicializando WebSocket:', error);
        reject(error);
      }
    });
  }

  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      switch (data.event) {
        case 'CONNECTED':
          if (this.onConnected) this.onConnected(data);
          break;

        case 'BUNDLE_VALIDATED':
          if (this.onBundleValidated) this.onBundleValidated(data);
          if (data.shouldRecordVideo === true) {
            if (this.pendingVideoBlob && this.pendingVideoBlob.size > 0) {
              this.sendVideo(this.pendingVideoBlob, 256 * 1024)
                .then(() => { this.pendingVideoBlob = null; })
                .catch((error) => { if (this.onError) this.onError({ type: 'VIDEO_SEND_ERROR', error }); });
            } else {
              if (this.onError) this.onError({ type: 'VIDEO_NOT_AVAILABLE', message: 'Video requerido pero no disponible' });
            }
          } else {
            this.pendingVideoBlob = null;
          }
          break;

        case 'VIDEO_UPLOADED':
          if (this.onVideoUploaded) this.onVideoUploaded(data);
          break;

        case 'COMPLETE': {
          const completeData = data.data || data;
          if (this.onComplete) this.onComplete(completeData);
          setTimeout(() => this.close(), 1000);
          break;
        }

        case 'ERROR':
          if (this.onError) this.onError(data);
          break;

        default:
          break;
      }
    } catch (error) {
      if (this.onError) this.onError({ type: 'PARSE_ERROR', error, raw: event.data });
    }
  }

  async sendBundle(bundleData) {
    if (!this.isConnected) throw new Error('WebSocket no está conectado');
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) throw new Error(`WebSocket no está listo (readyState=${this.ws?.readyState})`);

    let arrayBuffer;
    if (bundleData instanceof Blob) arrayBuffer = await bundleData.arrayBuffer();
    else if (bundleData instanceof ArrayBuffer) arrayBuffer = bundleData;
    else throw new Error('Bundle debe ser Blob o ArrayBuffer');

    const uint8Array = new Uint8Array(arrayBuffer);
    const compressed = pako.gzip(uint8Array);
    this.ws.send(compressed);
  }

  async sendBundleWithMetadata(bundleData, deviceMetadata) {
    await this.sendBundle(bundleData);
    if (deviceMetadata) {
      this.ws.send(JSON.stringify({ action: 'metadata', data: deviceMetadata }));
    }
  }

  async sendVideo(videoBlob, chunkSize = 64 * 1024) {
    if (!this.isConnected) throw new Error('WebSocket no está conectado');

    const chunks = [];
    let offset = 0;
    while (offset < videoBlob.size) {
      const chunk = videoBlob.slice(offset, offset + chunkSize);
      chunks.push(chunk);
      offset += chunkSize;
    }
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      const arrayBuffer = await chunks[i].arrayBuffer();
      this.ws.send(arrayBuffer);
      const progress = ((i + 1) / totalChunks) * 100;
      if (this.onVideoProgress) this.onVideoProgress({ current: i + 1, total: totalChunks, progress });
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    this.ws.send(JSON.stringify({ action: 'finalize_video' }));
  }

  close() {
    if (this.isRecordingParallel) {
      this.stopParallelRecording().catch(() => {});
    }

    if (this.mediaRecorder) {
      try { if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop(); } catch {}
      this.mediaRecorder = null;
    }

    if (this.mediaStream) {
      try { this.mediaStream.getTracks().forEach((t) => t.stop()); } catch {}
      this.mediaStream = null;
    }

    this.parallelVideoChunks = [];
    this.videoChunks = [];

    if (this.ws) {
      this.isConnected = false;
      try {
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close(1000, 'Process completed');
        }
      } catch {}
      this.ws = null;
    }
  }

  async startParallelRecording(stream) {
    if (this.isRecordingParallel) return;

    this.mediaStream = stream;
    this.parallelVideoChunks = [];
    this.recordingStartTime = Date.now(); // Registrar tiempo de inicio

    const mimeTypes = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm;codecs=h264',
      'video/webm',
      'video/mp4'
    ];

    let selectedMimeType = 'video/webm';
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        break;
      }
    }

    const options = { mimeType: selectedMimeType, videoBitsPerSecond: 500000 };
    this.mediaRecorder = new MediaRecorder(stream, options);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) this.parallelVideoChunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => { this.isRecordingParallel = false; };
    this.mediaRecorder.onerror = () => { this.isRecordingParallel = false; };

    this.mediaRecorder.start(250);
    this.isRecordingParallel = true;
  }

  async stopParallelRecording() {
    const MIN_RECORDING_DURATION = 2000; // 5 segundos mínimo

    if (!this.isRecordingParallel || !this.mediaRecorder) {
      if (this.parallelVideoChunks && this.parallelVideoChunks.length > 0) {
        const videoBlob = new Blob(this.parallelVideoChunks, { type: 'video/webm' });
        this.parallelVideoChunks = [];
        this.recordingStartTime = null;
        return videoBlob;
      }
      return null;
    }

    return new Promise((resolve) => {
      // Calcular tiempo transcurrido desde el inicio
      const elapsedTime = this.recordingStartTime ? Date.now() - this.recordingStartTime : 0;
      const remainingTime = Math.max(0, MIN_RECORDING_DURATION - elapsedTime);

      console.log(`[WS] Tiempo grabado: ${elapsedTime}ms, esperando ${remainingTime}ms más para cumplir mínimo de ${MIN_RECORDING_DURATION}ms`);

      // Esperar el tiempo restante antes de detener la grabación
      setTimeout(() => {
        const timeoutId = setTimeout(() => {
          this.isRecordingParallel = false;
          if (this.parallelVideoChunks && this.parallelVideoChunks.length > 0) {
            const videoBlob = new Blob(this.parallelVideoChunks, { type: this.mediaRecorder?.mimeType || 'video/webm' });
            this.parallelVideoChunks = [];
            this.recordingStartTime = null;
            resolve(videoBlob);
          } else {
            this.recordingStartTime = null;
            resolve(null);
          }
        }, 5000);

        this.mediaRecorder.onstop = () => {
          clearTimeout(timeoutId);
          this.isRecordingParallel = false;
          const videoBlob = new Blob(this.parallelVideoChunks, { type: this.mediaRecorder.mimeType || 'video/webm' });
          this.parallelVideoChunks = [];
          this.recordingStartTime = null;
          console.log(`[WS] Video finalizado: ${videoBlob.size} bytes`);
          resolve(videoBlob);
        };

        if (this.mediaRecorder.state === 'recording') this.mediaRecorder.stop();
        else {
          clearTimeout(timeoutId);
          this.isRecordingParallel = false;
          if (this.parallelVideoChunks && this.parallelVideoChunks.length > 0) {
            const videoBlob = new Blob(this.parallelVideoChunks, { type: this.mediaRecorder?.mimeType || 'video/webm' });
            this.parallelVideoChunks = [];
            this.recordingStartTime = null;
            resolve(videoBlob);
          } else {
            this.recordingStartTime = null;
            resolve(null);
          }
        }
      }, remainingTime);
    });
  }

  async stopParallelRecordingAndUpload() {
    const videoBlob = await this.stopParallelRecording();
    if (!videoBlob || videoBlob.size === 0) return;
    await this.uploadVideoByChunks(videoBlob, 256 * 1024);
  }

  async captureWithParallelVideo(captureBundleCallback, cameraStream) {
    await this.startParallelRecording(cameraStream);
    const bundleData = await captureBundleCallback();
    const videoBlob = await this.stopParallelRecording();
    return { bundleData, videoBlob };
  }

  async recordAndUploadVideo(duration = 5000) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });

      let mimeType = 'video/webm;codecs=vp8';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2500000 });
      const chunks = [];

      const recordingPromise = new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        mediaRecorder.onstop = async () => {
          const videoBlob = new Blob(chunks, { type: mimeType });
          stream.getTracks().forEach((t) => t.stop());
          await this.uploadVideoByChunks(videoBlob);
          resolve();
        };
        mediaRecorder.onerror = (evt) => reject(evt.error);
      });

      mediaRecorder.start();
      setTimeout(() => mediaRecorder.stop(), duration);
      await recordingPromise;
    } catch (error) {
      if (this.onError) this.onError({ type: 'RECORD_ERROR', error });
      throw error;
    }
  }

  async uploadVideoByChunks(videoBlob, chunkSize = 65536) {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket no está conectado');
    }

    let offset = 0;
    let chunkNumber = 0;
    const totalChunks = Math.ceil(videoBlob.size / chunkSize);

    while (offset < videoBlob.size) {
      const chunk = videoBlob.slice(offset, offset + chunkSize);
      const arrayBuffer = await chunk.arrayBuffer();
      chunkNumber += 1;
      this.ws.send(arrayBuffer);
      offset += chunkSize;
      const progress = Math.min(100, (offset / videoBlob.size) * 100);
      if (this.onVideoProgress) {
        this.onVideoProgress({ current: chunkNumber, total: totalChunks, progress, bytesUploaded: Math.min(offset, videoBlob.size), totalBytes: videoBlob.size });
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    this.ws.send(JSON.stringify({ action: 'finalize_video' }));
  }

  async captureAndSendParallel(captureBundleCallback, cameraStream, deviceMetadata = null) {
    const { bundleData, videoBlob } = await this.captureWithParallelVideo(captureBundleCallback, cameraStream);
    await this.sendBundleWithMetadata(bundleData, deviceMetadata);
    if (videoBlob && videoBlob.size > 0) await this.sendVideo(videoBlob, 256 * 1024);
  }

  async stopRecordingAndSend(bundleData, deviceMetadata = null) {
    if (this.isRecordingParallel) this.pendingVideoBlob = await this.stopParallelRecording();
    else this.pendingVideoBlob = null;
    await this.sendBundleWithMetadata(bundleData, deviceMetadata);
  }

  async sendBundleAsJSON(bundleData, deviceMetadata = null) {
    if (!this.isSocketConnected()) throw new Error('WebSocket no está conectado');

    let arrayBuffer;
    if (bundleData instanceof Blob) arrayBuffer = await bundleData.arrayBuffer();
    else if (bundleData instanceof ArrayBuffer) arrayBuffer = bundleData;
    else throw new Error('Bundle debe ser Blob o ArrayBuffer');

    const base64 = this._arrayBufferToBase64(arrayBuffer);
    this.ws.send(JSON.stringify({ type: 'bundle', data: base64, filename: 'selfie.jpg' }));

    if (deviceMetadata) {
      this.ws.send(JSON.stringify({ action: 'metadata', data: deviceMetadata }));
    }
  }

  async sendVideoAsJSON(videoBlob) {
    if (!this.isSocketConnected()) throw new Error('WebSocket no está conectado');
    const base64 = await this._blobToBase64(videoBlob);
    this.ws.send(JSON.stringify({ type: 'video', data: base64, filename: 'liveness_video.webm' }));
  }

  async stopRecordingAndSendJSON(bundleData, deviceMetadata = null) {
    let videoBlob = null;
    if (this.isRecordingParallel) videoBlob = await this.stopParallelRecording();
    await this.sendBundleAsJSON(bundleData, deviceMetadata);
    if (videoBlob && videoBlob.size > 0) await this.sendVideoAsJSON(videoBlob);
  }

  _arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export async function createlivenessClient(processId, baseUrl, callbacks = {}) {
  const client = new livenessWebSocketClient(processId, baseUrl, callbacks);
  await client.connect();
  return client;
}

export default livenessWebSocketClient;
